/**
 * Policy Pack Manager - Full implementation of Policy API
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { 
  listPolicyPacks,
  getPolicyPack,
  deletePolicyPack,
  createPolicyPack,
  validatePolicyPack,
} from '../../api/policy';
import { useWorkspace } from '../../hooks/useWorkspace';
import { 
  Shield, 
  Upload, 
  Trash2, 
  Eye, 
  CheckCircle, 
  AlertCircle,
  FileText,
  Download,
} from 'lucide-react';

export function PolicyManager() {
  const { tenantId } = useWorkspace();
  const queryClient = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<any>(null);

  const { data: policyPacks, isLoading } = useQuery({
    queryKey: ['policy-packs', tenantId],
    queryFn: () => listPolicyPacks(tenantId || undefined),
    enabled: true,
  });

  const deleteMutation = useMutation({
    mutationFn: (policyPackId: string) => deletePolicyPack(policyPackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-packs'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (file: File) => createPolicyPack(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-packs'] });
      setShowUpload(false);
      setSelectedFile(null);
    },
  });

  const validateMutation = useMutation({
    mutationFn: (file: File) => validatePolicyPack(file),
    onSuccess: (result) => {
      setValidationResult(result);
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-validate
      await validateMutation.mutateAsync(file);
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      await createMutation.mutateAsync(selectedFile);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Policy Packs</h1>
            <p className="text-gray-600 mt-1">
              {policyPacks?.length || 0} policy pack{policyPacks?.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Upload className="h-4 w-4" />
          Upload Policy Pack
        </Button>
      </div>

      {/* Policy Packs List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {policyPacks?.map((pack) => (
          <Card key={pack.id}>
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{pack.name}</h3>
                  <p className="text-sm text-gray-600">{pack.description}</p>
                </div>
                <Badge variant="success">v{pack.version}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-500 text-xs">Tenant</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{pack.tenant_id}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">Created</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {new Date(pack.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-gray-200">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // View policy pack data
                    console.log(pack.policy_data);
                  }}
                  className="flex-1"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (window.confirm(`Delete policy pack "${pack.name}"?`)) {
                      deleteMutation.mutate(pack.id);
                    }
                  }}
                  className="text-red-600 hover:text-red-700"
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUpload(false)}>
          <div className="max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Upload Policy Pack</h3>
                  <button
                    onClick={() => setShowUpload(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select YAML File
                  </label>
                  <input
                    type="file"
                    accept=".yaml,.yml"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                {validationResult && (
                  <div className={`border rounded-lg p-4 ${
                    validationResult.valid
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {validationResult.valid ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-medium text-green-900">Valid Policy Pack</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-5 w-5 text-red-600" />
                          <span className="font-medium text-red-900">Validation Errors</span>
                        </>
                      )}
                    </div>
                    {validationResult.errors?.map((err: string, idx: number) => (
                      <div key={idx} className="text-sm text-red-700 mt-1">
                        • {err}
                      </div>
                    ))}
                    {validationResult.warnings?.map((warn: string, idx: number) => (
                      <div key={idx} className="text-sm text-yellow-700 mt-1">
                        ⚠ {warn}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowUpload(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || !validationResult?.valid || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Uploading...' : 'Create Policy Pack'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

